import { Component, EventEmitter, HostListener, Input, OnInit, Output, inject } from '@angular/core';
import { InventoryPageAccessService } from 'src/app/modules/inventory/services/inventory-page-access.service';
import { Router } from '@angular/router';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LocationSelectionService } from 'src/app/modules/commons/location-selection/service/location-selection.service';
import { QualityGradeService } from 'src/app/services/quality-grade/quality-grade.service';
import { ProductService } from 'src/app/modules/product/services/product.service';
import { PermissionService } from 'src/app/services/permission.service';
import { HttpParams } from '@angular/common/http';
import { parseQuickSearch, resolveAiGuess, QuickSearchCountry, QuickSearchGrade, QuickSearchProduct, QuickSearchQuery, QuickSearchTenantDefaults } from './quick-search-parser';
import { QuickSearchService, QuickSearchResult, FloorNowRow, UpcomingConsignmentRow } from './quick-search.service';
import { AiQuickSearchService, AiQuickSearchResponse } from './ai-quick-search.service';
import { TallyDefaultSettingsService } from 'src/app/modules/extras/application-settings/sections/tally-default-settings.service';
import { MessageService } from 'primeng/api';
import { ConsignmentRef, PendingStockInGroup, PendingStockInRow, StockGroup } from 'src/app/modules/inventory/model/inventory.model';
import { InventoryLotService } from 'src/app/modules/inventory/services/inventory-lot.service';
import { QUICK_SEARCH_PURCHASING_FLAGS } from './quick-search-purchasing-flags';
import { SimpleCalculatorComponent } from 'src/app/commons/components/simple-calculator/simple-calculator.component';

const RECENT_SEARCH_KEY = 'quickSearch.recent';
const RECENT_SEARCH_MAX = 5;
/** 1 CBM = this many CFT — used only for the price/CBM ⇄ price/CFT quick-switch, plain client
 *  math, no backend involvement. */
const CFT_PER_CBM = 35.3147;

@Component({
  selector: 'app-quick-search',
  templateUrl: './quick-search.component.html',
  styleUrls: ['./quick-search.component.css'],
  standalone: true,
  imports: [SHARED_MODULES, SimpleCalculatorComponent],
  providers: [MessageService],
})
export class QuickSearchComponent implements OnInit {
  /** 2026-09-16 — a row only opens Overview / Processing when the user may open that page
   *  (hidden = no access); otherwise the row stays read-only information. */
  readonly pageAccess = inject(InventoryPageAccessService);

  /** True when mounted inside app-unified-search's Stock tab — see GlobalSearchComponent's own
   *  identical field comment for the reasoning (suppresses own trigger/dialog/input, shell owns
   *  those; default false keeps standalone usage unaffected). */
  @Input() embedded = false;

  /** Fired instead of opening this component's own (suppressed-when-embedded) calculator dialog
   *  when embedded — see openCalculatorDialog(). The host shell (UnifiedSearchComponent) owns the
   *  actual calculator UI in embedded mode; this is the bridge that was missing, which is why the
   *  per-row calculator button silently did nothing inside the merged search dialog (real
   *  incident, 2026-09-06). */
  @Output() calculatorRequested = new EventEmitter<{ startValue: number | null; startLabel: string | null; currency: string | null }>();

  visible = false;
  queryText = '';
  query: QuickSearchQuery | null = null;
  result: QuickSearchResult | null = null;
  searching = false;
  searched = false;

  // ── Recent searches — Application Settings-style "Reset to Default" isn't relevant here; this
  // is a personal, browser-local convenience list, never shared/synced, capped at 5 (enough for
  // a shift's worth of repeat requests without crowding the compact dialog). Captured on close,
  // not per keystroke — a debounce tick would pollute the list with half-typed fragments. A
  // recent pill always replays as a plain (free) search, even if the original used Ask AI —
  // silently re-spending AI credits from a one-tap history click would be a real surprise-cost
  // problem, not a convenience.
  recentSearches: string[] = [];

  // ── Ask AI (Mill Command Center plan, §F/§H) — explicit opt-in, never fired per keystroke;
  // each call costs real tokens, so it only runs when the user deliberately asks for it.
  aiMode = false;
  aiSearching = false;
  aiCredits: { usedThisCall: number; usedTotal: number; total: number; usagePct: number | null } | null = null;
  aiError: string | null = null;

  // ── "How this works" recap — a tap-to-try mini-guide instead of a wall of text, so a first-time
  // (or rusty) user can actually learn the grammar by trying it, not just reading about it.
  showHelp = false;

  // ── Row expand (purchasing details) — key is a per-row identity string (see rowKey()), tracked
  // in a Set rather than on each row object so switching search results never needs to reconcile
  // expand state against new object identities. ──
  readonly flags = QUICK_SEARCH_PURCHASING_FLAGS;
  private expandedKeys = new Set<string>();
  // 'loading' while the on-demand breakdown fetch is in flight for that row; a resolved array once
  // it lands. Never populated eagerly — only when a mixed row is actually expanded.
  breakdowns: Record<string, ConsignmentRef[] | 'loading'> = {};

  // ── Quick price (Qty x Price/CBM = Total, plus Price/CBM <-> Price/CFT) — plain inline fields
  // inside the expand panel, same on every screen size. A plain number input already gives phones
  // their native numeric keyboard for free (inputmode="decimal"), so this needs nothing custom. ──
  private calculatorOpenKeys = new Set<string>();
  calcPricePerCbm: Record<string, number | null> = {};

  // ── Real calculator — a genuine tap-or-type 4-function calculator (Windows/iOS Calculator
  // shaped), opened from a dedicated icon, hosted in its own <p-dialog> (never a popover/overlay
  // anchored to the trigger button — that's exactly what broke visually the first time this was
  // built; a dialog is centered on the viewport with no anchor math to get wrong, and it's the
  // same container the main Quick Search dialog itself already uses without incident). One shared
  // instance, like the search dialog itself — only one calculator can sensibly be open at a time. ──
  calculatorDialogVisible = false;
  calculatorStartValue: number | null = null;
  calculatorStartLabel: string | null = null;
  calculatorCurrency: string | null = null;

  // ── Arrow-key navigation (parity with Global Search's flatResults pattern) — a flat index
  // spanning all six result buckets in the same top-to-bottom order the template already renders
  // them, so ArrowUp/ArrowDown/Enter work uniformly without touching each bucket's own expand/
  // quick-price row state (those stay mouse/click-only, unaffected by this).
  highlightedIndex = -1;

  private countries: QuickSearchCountry[] = [];
  private grades: QuickSearchGrade[] = [];
  private products: QuickSearchProduct[] = [];
  private tenantDefaults: QuickSearchTenantDefaults | null = null;
  private candidatesLoaded = false;

  private textChange$ = new Subject<string>();

  constructor(
    private locationSelectionService: LocationSelectionService,
    private qualityGradeService: QualityGradeService,
    private productService: ProductService,
    private quickSearchService: QuickSearchService,
    private aiQuickSearchService: AiQuickSearchService,
    private inventoryLotService: InventoryLotService,
    private tallyDefaultSettingsService: TallyDefaultSettingsService,
    private messageService: MessageService,
    public permissionService: PermissionService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.textChange$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(text => this.runSearch(text));
    this.loadRecentSearches();
  }

  get canUse(): boolean {
    return this.permissionService.hasEffective('QUICK_SEARCH_ACCESS');
  }

  /** "/" opens Quick Search — same convention as Linear/GitHub/Notion. Ignored while the dialog
   *  is already open, while the user is typing in ANY input/textarea/select/contenteditable
   *  anywhere on the page (never steal a literal "/" keystroke from a form field), or without
   *  QUICK_SEARCH_ACCESS (matches the trigger button's own gating). */
  @HostListener('document:keydown', ['$event'])
  handleSlashShortcut(event: KeyboardEvent): void {
    if (this.embedded) return; // shell owns "/" when merged — see the `embedded` field comment
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
    if (this.visible || !this.canUse) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
    event.preventDefault();
    this.open();
  }

  open(): void {
    this.visible = true;
    this.showHelp = false;
    this.loadCandidatesOnce();
  }

  close(): void {
    this.visible = false;
    this.highlightedIndex = -1;
    this.saveRecentSearch(this.queryText);
  }

  toggleHelp(): void {
    this.showHelp = !this.showHelp;
  }

  onTextChange(): void {
    // A fresh edit invalidates any AI answer for the previous text — never show a stale
    // AI-resolved result against text the user has since changed.
    this.aiError = null;
    this.highlightedIndex = -1;
    this.textChange$.next(this.queryText);
  }

  /** Called by the shell (embedded mode only) — see GlobalSearchComponent.setQuery()'s identical
   *  reasoning: reuses the exact same debounced pipeline a typed keystroke would trigger. */
  setQuery(text: string): void {
    this.queryText = text;
    this.onTextChange();
  }

  /** Every bucket in template render order (Available → Pending Stock-In → In Process → Arriving
   *  → Departing → Preparing), flattened into one array with a uniform open() per item — the
   *  building block both the template's highlight classes and onInputKeydown below index into. */
  get flatRows(): Array<{ kind: 'available' | 'pending' | 'process' | 'arriving' | 'departing' | 'preparing'; open: () => void }> {
    const rows: Array<{ kind: 'available' | 'pending' | 'process' | 'arriving' | 'departing' | 'preparing'; open: () => void }> = [];
    (this.result?.available ?? []).forEach(g => rows.push({ kind: 'available', open: () => this.openAvailableGroup(g) }));
    (this.result?.pendingStockIn ?? []).forEach(g => rows.push({ kind: 'pending', open: () => this.openPendingGroup(g) }));
    (this.result?.inProcess ?? []).forEach(r => rows.push({ kind: 'process', open: () => this.openInProcess(r) }));
    const radar = this.result?.arriving;
    (radar?.arriving ?? []).forEach(c => rows.push({ kind: 'arriving', open: () => this.openConsignment(c) }));
    (radar?.departing ?? []).forEach(c => rows.push({ kind: 'departing', open: () => this.openConsignment(c) }));
    (radar?.preparing ?? []).forEach(c => rows.push({ kind: 'preparing', open: () => this.openConsignment(c) }));
    return rows;
  }

  /** Base offset of each bucket into flatRows — lets the template compute a row's global index as
   *  `xBaseIndex + i` from its own existing *ngFor="let x of ...; let i = index" without needing to
   *  restructure the six separate loops into one. */
  get availableBaseIndex(): number { return 0; }
  get pendingBaseIndex(): number { return (this.result?.available?.length ?? 0); }
  get processBaseIndex(): number { return this.pendingBaseIndex + (this.result?.pendingStockIn?.length ?? 0); }
  get arrivingBaseIndex(): number { return this.processBaseIndex + (this.result?.inProcess?.length ?? 0); }
  get departingBaseIndex(): number { return this.arrivingBaseIndex + (this.result?.arriving?.arriving?.length ?? 0); }
  get preparingBaseIndex(): number { return this.departingBaseIndex + (this.result?.arriving?.departing?.length ?? 0); }

  isRowHighlighted(globalIndex: number): boolean {
    return this.highlightedIndex === globalIndex;
  }

  /** Sum of every group's unitCount — the bucket label shows this (real total pending units)
   *  alongside the group count only when they differ, so a search with no multi-unit groups keeps
   *  showing the exact same single number it always did ("Pending Stock-In — 1"). */
  get pendingTotalUnits(): number {
    return (this.result?.pendingStockIn ?? []).reduce((sum, g) => sum + g.unitCount, 0);
  }

  onInputKeydown(event: KeyboardEvent): void {
    // While the calculator overlay is open, it owns all keyboard input — same reasoning as
    // GlobalSearchComponent.onInputKeydown's own comment.
    if (this.calculatorDialogVisible) return;
    const flat = this.flatRows;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (flat.length) this.highlightedIndex = (this.highlightedIndex + 1) % flat.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (flat.length) this.highlightedIndex = (this.highlightedIndex - 1 + flat.length) % flat.length;
    } else if (event.key === 'Enter') {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < flat.length) {
        event.preventDefault();
        flat[this.highlightedIndex].open();
      }
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  /** Tap-to-try — every example in the help view is a real, runnable query, not decorative text.
   *  Fills the box and searches exactly as if the user had typed it themselves. */
  tryExample(example: string): void {
    this.showHelp = false;
    this.queryText = example;
    this.onTextChange();
  }

  toggleAiMode(): void {
    this.aiMode = !this.aiMode;
    this.aiError = null;
  }

  /** A history pill always runs the plain deterministic search, never Ask AI — see the
   *  recentSearches field comment for why. */
  useRecentSearch(text: string): void {
    this.queryText = text;
    this.aiError = null;
    this.runSearch(text);
  }

  removeRecentSearch(text: string, event: Event): void {
    event.stopPropagation();
    this.recentSearches = this.recentSearches.filter(t => t !== text);
    this.persistRecentSearches();
  }

  private loadRecentSearches(): void {
    try {
      const raw = localStorage.getItem(RECENT_SEARCH_KEY);
      this.recentSearches = raw ? JSON.parse(raw) : [];
    } catch {
      this.recentSearches = [];
    }
  }

  private saveRecentSearch(text: string): void {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    this.recentSearches = [trimmed, ...this.recentSearches.filter(t => t !== trimmed)].slice(0, RECENT_SEARCH_MAX);
    this.persistRecentSearches();
  }

  private persistRecentSearches(): void {
    try {
      localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(this.recentSearches));
    } catch { /* localStorage unavailable (private browsing etc.) — recent list just won't persist */ }
  }

  /** Explicit action — never fired per keystroke, unlike the deterministic path. Each call
   *  costs real tokens (Mill Command Center plan, §H). */
  askAi(): void {
    const trimmed = this.queryText.trim();
    if (!trimmed || this.aiSearching || !this.canUse) return;
    this.aiSearching = true;
    this.aiError = null;
    this.aiQuickSearchService.parse(trimmed).subscribe({
      next: (resp: AiQuickSearchResponse) => {
        this.aiSearching = false;
        this.aiCredits = { usedThisCall: resp.creditsUsedThisCall, usedTotal: resp.creditsUsedTotal, total: resp.creditsTotal, usagePct: resp.usagePct };
        this.notifyCreditThreshold(resp.usagePct);

        this.query = resolveAiGuess(trimmed, resp, this.countries, this.grades, this.products, this.tenantDefaults);
        this.highlightedIndex = -1;
        this.searching = true;
        this.quickSearchService.search(this.query, this.tenantDefaults).subscribe({
          next: result => { this.result = result; this.searching = false; this.searched = true; },
          error: () => { this.result = null; this.searching = false; this.searched = true; },
        });
      },
      error: (err) => {
        this.aiSearching = false;
        this.aiError = err?.status === 402
          ? 'quickSearch.ai.outOfCredits'
          : err?.status === 429
            ? 'quickSearch.ai.rateLimited'
            : err?.status === 403
              ? 'quickSearch.ai.forestOnly'
              : 'quickSearch.ai.genericError';
      },
    });
  }

  /** Threshold banner, matching what was asked for: tell the user at 80/90/95/100% — never
   *  silently let a tenant run out without warning. Fires once per call that crosses a new
   *  threshold, not on every render. */
  private notifyCreditThreshold(usagePct: number | null): void {
    if (usagePct == null) return;
    if (usagePct >= 100) {
      this.messageService.add({ severity: 'error', summary: 'AI credits exhausted', detail: 'Regular search still works with no credit cost.' });
    } else if (usagePct >= 95) {
      this.messageService.add({ severity: 'warn', summary: `${usagePct}% of AI credits used` });
    } else if (usagePct >= 90) {
      this.messageService.add({ severity: 'warn', summary: `${usagePct}% of AI credits used` });
    } else if (usagePct >= 80) {
      this.messageService.add({ severity: 'info', summary: `${usagePct}% of AI credits used` });
    }
  }

  private loadCandidatesOnce(): void {
    // 2026-09-16 — hidden = no access. In the merged search box this component is always created
    // (just hidden) and open() is called for everyone, including users with only Global Search.
    if (this.candidatesLoaded || !this.canUse) return;
    this.candidatesLoaded = true;

    this.locationSelectionService.getCountries().subscribe({
      next: (countries: any[]) => {
        this.countries = (countries || [])
          .map(c => ({ name: c.name || c.countryName || '', countryCode: String(c.countryCode || '') }))
          .filter(c => c.name && c.countryCode);
      },
      error: () => { /* origin matching degrades gracefully — same as Quick Tally */ },
    });

    this.qualityGradeService.list().subscribe({
      next: grades => { this.grades = grades; },
      error: () => { /* quality matching degrades gracefully */ },
    });

    // Same "fetch a large page once" pattern used elsewhere for candidate lists (e.g. the
    // Machines form's Location dropdown) — a real paginated product catalog browser is a
    // different concern from a fuzzy-match candidate list. isRound (product.types) is the real
    // signal ambiguous size mentions ("200mm") resolve against, instead of a guessed range.
    const params = new HttpParams().set('size', '500').set('page', '0');
    this.productService.getProductsList(params).subscribe({
      next: page => {
        this.products = (page.content || []).map(p => ({
          id: p.productId, name: p.name,
          isRound: p.types === 'ROUND' ? true : p.types === 'SQUARE' ? false : null,
        }));
      },
      error: () => { /* species matching degrades to dimensions/origin/quality only */ },
    });

    // Tenant's own configured UoM/tolerance defaults (Application Settings → Tally Defaults) —
    // drives the bare-shorthand assumed-unit chip and the match-tolerance band. Degrades to the
    // parser/service's own hardcoded literal fallback if this fails, same as every other
    // candidate list here.
    this.tallyDefaultSettingsService.get().subscribe({
      next: res => { this.tenantDefaults = res; },
      error: () => { /* stays null — parser/service already fall back to literal defaults */ },
    });
  }

  private runSearch(text: string): void {
    const trimmed = (text || '').trim();
    if (!trimmed || !this.canUse) { // no QUICK_SEARCH_ACCESS → never search (hidden = no access)
      this.query = null;
      this.result = null;
      this.searched = false;
      return;
    }
    this.query = parseQuickSearch(trimmed, this.countries, this.grades, this.products, this.tenantDefaults);
    this.searching = true;
    this.quickSearchService.search(this.query, this.tenantDefaults).subscribe({
      next: result => { this.result = result; this.searching = false; this.searched = true; },
      error: () => { this.result = null; this.searching = false; this.searched = true; },
    });
  }

  /** User picked one candidate off a "Did you mean?" list — locks it in and re-runs the search
   *  against the already-parsed query (dimensions/origin/quality stay intact), rather than
   *  re-parsing the raw text, which would just reproduce the same ambiguity. */
  selectAmbiguousSpecies(candidate: { id: number; name: string }): void {
    if (!this.query) return;
    this.query.productId = candidate.id;
    this.query.productName = candidate.name;
    const matched = this.products.find(p => p.id === candidate.id);
    this.query.productIsRound = matched ? matched.isRound : null;
    this.query.matches = this.query.matches
      .filter(m => m.field !== 'species-ambiguous')
      .concat([{ field: 'species', label: candidate.name, fuzzy: true }]);
    this.query.ambiguousSpecies = [];
    this.highlightedIndex = -1;
    this.searching = true;
    this.quickSearchService.search(this.query, this.tenantDefaults).subscribe({
      next: result => { this.result = result; this.searching = false; this.searched = true; },
      error: () => { this.result = null; this.searching = false; this.searched = true; },
    });
  }

  get hasAnyResult(): boolean {
    if (!this.result) return false;
    const arriving = this.result.arriving;
    return this.result.available.length > 0
      || this.result.inProcess.length > 0
      || this.result.pendingStockIn.length > 0
      || !!(arriving && (arriving.preparing.length || arriving.departing.length || arriving.arriving.length));
  }

  /** True when this search left out a source because the company's plan does not include it ('stock' = Inventory,
   *  'mill' = Mill Jobs). Shown as one quiet line, never a dialog. */
  isLocked(source: 'stock' | 'mill'): boolean {
    return !!this.result?.lockedSources?.includes(source);
  }

  /** The quiet line's "See plans" link: closes the search and opens the Subscription page. */
  openPlans(): void {
    this.close();
    this.router.navigate(['/subscriptions']);
  }

  /** A "PENDING STOCK-IN" unit is never itself a receivable action from here — it just points the
   *  user at the real Stock Unit so they can review and Receive it themselves (never auto-received
   *  from a search result). Closes the dialog since the user is navigating away regardless. */
  openPendingStockIn(row: PendingStockInRow): void {
    this.close();
    this.router.navigate(['/new-tallysheet/transport-unit'], { queryParams: { transportId: row.transportUnitEncodedId } });
  }

  /** A Pending Stock-In row is now a group (same grouping InventoryLotServiceImpl.getStockGroups
   *  already does for Available Now). A single-unit group — today's common case — behaves exactly
   *  as before grouping existed: one click straight to that Transport Unit. A multi-unit group has
   *  no single correct destination, so a click/Enter here toggles the expand panel instead, same
   *  as clicking the row in the template (see quick-search.component.html). */
  openPendingGroup(g: PendingStockInGroup): void {
    if (g.unitCount === 1 && g.units[0]) {
      this.openPendingStockIn(g.units[0]);
    } else {
      this.toggleExpand(this.rowKey(g, 'pending-group'));
    }
  }

  /** An "Available Now" row is a summed-up group (product × size × quality × origin) — it can be
   *  backed by several lots, unlike a Pending Stock-In row which is always exactly one Transport
   *  Unit. There's no single "the" thing to open in general, so this jumps to the group's oldest
   *  contributing lot (lots are already sorted oldest-first — see StockGroupLot) via Inventory
   *  Overview's existing ?openLot= deep link (the same one "View Lot" links elsewhere already use)
   *  — reusing a proven destination rather than inventing a new one. */
  openAvailableGroup(g: StockGroup): void {
    const lotEncodedId = g.lots?.[0]?.lotEncodedId;
    if (!lotEncodedId || !this.pageAccess.canOpen('overview')) return;
    this.close();
    this.router.navigate(['/inventory/overview'], { queryParams: { openLot: lotEncodedId } });
  }

  /** Plain view-only deep link — see InventoryProcessingComponent.handleViewRunParam()'s own
   *  comment for why this is a separate query param from the existing ?runId=/&returnFrom=tally
   *  one (that one pre-fills a specific "review re-saw output" dialog, wrong context here). */
  openInProcess(r: FloorNowRow): void {
    if (!this.pageAccess.canOpen('processing')) return;
    this.close();
    this.router.navigate(['/inventory/processing'], { queryParams: { viewRun: r.runEncodedId } });
  }

  openConsignment(c: UpcomingConsignmentRow): void {
    this.close();
    this.router.navigate(['/consignments/consignment-view', c.encodedId]);
  }

  /** Reads the group's own width/thickness/girth (the grouping key, so always a single value) and
   *  its length as a min-of-range (mirrors StockGroup.squareDims's own "show the low end"
   *  convention for a length range) — same formatting a plain PendingStockInRow used before
   *  grouping existed. */
  pendingGroupDims(g: PendingStockInGroup): string {
    if (g.round) {
      const girth = g.girthMm != null ? (g.girthMm / 10).toFixed(0) : '?';
      const l = g.roundLengthMinMm != null ? (g.roundLengthMinMm / 10).toFixed(0) : '?';
      return `${girth}cm × ${l}cm`;
    }
    const w = g.widthMm != null ? (g.widthMm / 25.4).toFixed(0) : '?';
    const t = g.thicknessMm != null ? (g.thicknessMm / 25.4).toFixed(0) : '?';
    const l = g.lengthMinMm != null ? (g.lengthMinMm / 304.8).toFixed(0) : '?';
    return `${w}″×${t}″×${l}ft`;
  }

  squareDims(g: StockGroup): string {
    if (g.isRound || g.widthMm == null || g.thicknessMm == null) return '';
    const w = (g.widthMm / 25.4).toFixed(0);
    const t = (g.thicknessMm / 25.4).toFixed(0);
    const l = g.lengthMinMm != null ? (g.lengthMinMm / 304.8).toFixed(0) : '?';
    return `${w}″×${t}″×${l}ft`;
  }

  // ── Row expand (purchasing details) ──────────────────────────────────────────────────────

  /** An individual Pending Stock-In unit is a real Transport Unit (a stable, unique id already);
   *  Available Now rows AND Pending Stock-In groups are both server-summed buckets with no id of
   *  their own, so a same-shaped-attributes key (mirroring dedupeGroups'/dedupePendingStockIn's own
   *  keys in quick-search.service.ts) is used instead. */
  rowKey(item: PendingStockInRow | PendingStockInGroup | StockGroup, kind: 'pending' | 'pending-group' | 'available'): string {
    if (kind === 'pending') return 'p:' + (item as PendingStockInRow).transportUnitEncodedId;
    if (kind === 'pending-group') {
      const g = item as PendingStockInGroup;
      return ['pg', g.productId, g.siteId, g.originCode, g.qualityCode, g.widthMm, g.thicknessMm, g.girthMm].join('|');
    }
    const g = item as StockGroup;
    return ['a', g.productId, g.siteId, g.originCode, g.qualityCode, g.widthMm, g.thicknessMm, g.lengthMinMm, g.lengthMaxMm].join('|');
  }

  isExpanded(key: string): boolean {
    return this.expandedKeys.has(key);
  }

  toggleExpand(key: string, group?: StockGroup): void {
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
      return;
    }
    this.expandedKeys.add(key);
    if (group?.mixedConsignments && this.flags.showMixedBreakdown && !this.breakdowns[key]) {
      this.loadBreakdown(key, group);
    }
  }

  private loadBreakdown(key: string, group: StockGroup): void {
    const lotEncodedIds = (group.lots || []).map(l => l.lotEncodedId).filter(Boolean);
    if (!lotEncodedIds.length) return;
    this.breakdowns[key] = 'loading';
    this.inventoryLotService.getConsignmentBreakdown(lotEncodedIds).subscribe({
      next: refs => { this.breakdowns[key] = refs; },
      error: () => { delete this.breakdowns[key]; },
    });
  }

  /** Translation key for the Bill No / Bill of Lading No label — mirrors the Consignment form's
   *  own isLocalTrade rule (blLocal is set server-side using the identical rule, never re-derived
   *  differently here). */
  blLabelKey(blLocal: boolean | null | undefined): string {
    return blLocal ? 'quickSearch.billNo' : 'quickSearch.billOfLadingNo';
  }

  /** Template helper — breakdowns[key] is 'loading' | ConsignmentRef[] | undefined; Angular
   *  templates can't narrow a union like this on their own. */
  asBreakdownList(value: ConsignmentRef[] | 'loading' | undefined): ConsignmentRef[] {
    return Array.isArray(value) ? value : [];
  }

  // ── Quick price ─────────────────────────────────────────────────────────────────────────

  isQuickPriceOpen(key: string): boolean {
    return this.calculatorOpenKeys.has(key);
  }

  /** Opens with a sensible starting Price/CBM when one genuinely exists (a real per-unit rate,
   *  never the Consignment's own invoice total — that's a whole-shipment figure, not a rate, and
   *  would be actively misleading here). Starts blank otherwise — the person on the call types
   *  their own quoted rate either way. */
  toggleQuickPrice(key: string, prefillPricePerCbm: number | null): void {
    if (this.calculatorOpenKeys.has(key)) {
      this.calculatorOpenKeys.delete(key);
      return;
    }
    this.calculatorOpenKeys.add(key);
    if (this.calcPricePerCbm[key] === undefined) {
      this.calcPricePerCbm[key] = prefillPricePerCbm;
    }
  }

  onPricePerCbmInput(key: string, value: number | null): void {
    this.calcPricePerCbm[key] = value;
  }

  /** The CFT field edits the same underlying Price/CBM value (converted), so there is only ever
   *  one number stored — no risk of the two fields drifting out of sync with each other. */
  onPricePerCftInput(key: string, value: number | null): void {
    this.calcPricePerCbm[key] = value != null ? value * CFT_PER_CBM : null;
  }

  pricePerCft(key: string): number | null {
    const perCbm = this.calcPricePerCbm[key];
    if (perCbm == null) return null;
    // Rounded here (not via a template pipe) — piping a formatted string into an editable
    // [ngModel] on a type="number" input causes a type mismatch and cursor-jump risk.
    return Math.round((perCbm / CFT_PER_CBM) * 10000) / 10000;
  }

  calcTotal(key: string, qtyCbm: number | null | undefined): number | null {
    const perCbm = this.calcPricePerCbm[key];
    return perCbm != null && qtyCbm != null ? perCbm * qtyCbm : null;
  }

  // ── Real calculator ─────────────────────────────────────────────────────────────────────

  /** startValue is a genuine convenience prefill only (a real per-unit rate when one exists,
   *  never the Consignment's own whole-shipment invoice total) — the calculator itself is a
   *  blank tool the user can do any calculation with regardless of what's prefilled. */
  openCalculatorDialog(startValue: number | null, startLabel: string | null, currency: string | null): void {
    // Embedded (inside UnifiedSearchComponent's Stock tab): this component's own calculator
    // dialog is deliberately suppressed (*ngIf="canUse && !embedded" in the template) — the shell
    // owns the real calculator UI, so hand it off instead of silently doing nothing.
    if (this.embedded) {
      this.calculatorRequested.emit({ startValue, startLabel, currency });
      return;
    }
    this.calculatorStartValue = startValue;
    this.calculatorStartLabel = startLabel;
    this.calculatorCurrency = currency;
    this.calculatorDialogVisible = true;
  }

  closeCalculatorDialog(): void {
    this.calculatorDialogVisible = false;
  }
}
